import { getApiUrl } from '@podus/utils';
import type { ApiResponse } from '@/types';
import { getToken } from './session';

/**
 * Thin client for NEW_PODUS_API.
 *
 * Every `/api/*` route sits behind `systemMiddleware`, which expects the
 * authorization header as `Bearer <systemKey>:<jwt>` — the same shape the auth
 * service uses. The system key identifies the calling product, the JWT
 * identifies the person.
 */

const SYSTEM_KEY = import.meta.env.VITE_PODUS_SYSTEM_KEY ?? 'G@V3l2023-new-podus-v001233-SyS';

/**
 * Normalised `…/api` base, tolerant of the env var being given with or without
 * the suffix. Falls back to `getApiUrl()` rather than a literal production host,
 * so running `pnpm dev:ambassador` with no `.env` talks to a local API instead
 * of writing to live data.
 */
export const API_BASE = (() => {
    const raw = import.meta.env.VITE_PODUS_API_URL ?? getApiUrl();
    const trimmed = String(raw).replace(/\/$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
})();

export class ApiError extends Error {
    readonly status: number;
    readonly payload: unknown;

    constructor(message: string, status: number, payload: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.payload = payload;
    }
}

function headers(): HeadersInit {
    const token = getToken();
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SYSTEM_KEY}:${token ?? ''}`,
    };
}

async function handle<T>(res: Response): Promise<ApiResponse<T>> {
    const body = await res.json().catch(() => null);

    if (!res.ok) {
        const message =
            (body as { response?: { message?: string }; message?: string } | null)?.response?.message ??
            (body as { message?: string } | null)?.message ??
            'Something went wrong. Please try again.';
        throw new ApiError(message, res.status, body);
    }

    return body as ApiResponse<T>;
}

async function request<T>(method: string, path: string, payload?: unknown): Promise<ApiResponse<T>> {
    const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: headers(),
        body: payload !== undefined ? JSON.stringify(payload) : undefined,
    });
    return handle<T>(res);
}

export const api = {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
};

/**
 * Download an authenticated CSV endpoint as a file.
 *
 * A plain `<a download>` can't carry the authorization header, so the response
 * is fetched and handed to the browser as an object URL instead.
 */
export async function downloadCsv(path: string, filename: string): Promise<void> {
    const res = await fetch(`${API_BASE}${path}`, { headers: headers() });
    if (!res.ok) {
        throw new ApiError('Could not prepare your download. Please try again.', res.status, null);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
